import { useState } from "react"
import {
  component,
  definition,
  context as contextApi,
  styles,
  wire,
} from "@uesio/ui"
import Dialog from "../dialog/dialog"
import IconButton from "../iconbutton/iconbutton"
import MarkDownField from "./markdownfield"

interface MarkdownPanelFieldProps {
  setValue?: (value: wire.FieldValue) => void
  value: wire.FieldValue
  mode?: contextApi.FieldMode
  readonly?: boolean
  label?: string
}

const StyleDefaults = Object.freeze({
  root: ["flex", "w-full", "items-center", "gap-1"],
  trigger: ["shrink-0", "text-primary-600"],
  // Matches uesio/io.field's own default readonly text box
  // (componentvariants/uesio/io/field/default.yaml) so this preview looks
  // like any other read-mode field in the row, instead of a bespoke style.
  preview: [
    "grid",
    "w-full",
    "items-center",
    "truncate",
    "rounded",
    "border",
    "border-transparent",
    "bg-primary-50/70",
    "px-2.5",
    "py-2",
    "text-sm",
    "text-slate-700",
  ],
})

const previewOf = (value: string) => {
  const flattened = value.replace(/\s+/g, " ").trim()
  return flattened.length > 60 ? `${flattened.slice(0, 60)}…` : flattened
}

// Prompt/system-prompt-style LONGTEXT+MARKDOWN content can be very long, so
// rendering it inline (e.g. inside a list-field table row) is unusable. This
// keeps the row compact and defers the actual read/edit surface to the
// platform's own Dialog panel, reusing MarkDownField for both rendering and
// editing rather than building a bespoke editor. Edits are held in local
// draft state and only committed to the row's value on "Save" (a user
// closing/dismissing the panel without saving discards their edits) so it's
// unambiguous when a change actually took effect, ahead of the outer record
// save.
const MarkdownPanelField: definition.UtilityComponent<
  MarkdownPanelFieldProps
> = (props) => {
  const { context, mode, readonly, setValue, label = "Content" } = props
  const value = (props.value as string) || ""
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)
  const isEditMode = !readonly && mode === "EDIT" && setValue
  const classes = styles.useUtilityStyleTokens(
    StyleDefaults,
    props,
    "uesio/io.markdownpanelfield",
  )

  const openPanel = () => {
    setDraft(value)
    setOpen(true)
  }

  const handleSave = () => {
    setValue?.(draft)
    setOpen(false)
  }

  const readMoreLabel = context.getLabel("uesio/io.read_more")
  const editTextLabel = context.getLabel("uesio/io.edit_text")
  const addTextLabel = context.getLabel("uesio/io.add_text")

  return (
    <div className={classes.root} style={{ minWidth: 0 }}>
      {value ? (
        <>
          <div className={classes.preview}>{previewOf(value)}</div>
          <IconButton
            context={context}
            className={classes.trigger}
            icon={isEditMode ? "edit" : "visibility"}
            label={`${isEditMode ? editTextLabel : readMoreLabel}: ${previewOf(value)}`}
            onClick={openPanel}
          />
        </>
      ) : isEditMode ? (
        <IconButton
          context={context}
          className={classes.trigger}
          icon="add"
          label={`${addTextLabel} ${label}`}
          onClick={openPanel}
        />
      ) : (
        <span>—</span>
      )}
      {open && (
        <Dialog
          context={context}
          title={label}
          width="900px"
          height="80vh"
          onClose={() => setOpen(false)}
          actions={
            isEditMode && (
              <component.Utility
                utilityKey="uesio/io.button"
                context={context}
                variant="uesio/io.primary"
                label={context.getLabel("uesio/io.save")}
                onClick={handleSave}
              />
            )
          }
        >
          <MarkDownField
            context={context}
            value={isEditMode ? draft : value}
            mode={isEditMode ? "EDIT" : "READ"}
            readonly={!isEditMode}
            setValue={
              isEditMode
                ? (newValue) => setDraft((newValue as string) || "")
                : undefined
            }
          />
        </Dialog>
      )}
    </div>
  )
}

export default MarkdownPanelField
